import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { signUpUser } from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { ENV } from "@/lib/config/env";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { YesChefLogo } from "@/components/common/YesChefLogo";
import { passwordSchema } from "@/lib/auth/password-schema";

const signUpSchema = z
  .object({
    firstName: z.string().min(1, { error: () => t`First name is required` }),
    lastName: z.string().min(1, { error: () => t`Last name is required` }),
    email: z.string().email({ error: () => t`Invalid email address` }),
    phone: z.string().optional(),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: () => t`Passwords do not match`,
    path: ["confirmPassword"],
  });

type SignUpForm = z.infer<typeof signUpSchema>;

export const Route = createFileRoute("/(public)/sign-up")({
  component: SignUpComponent,
});

function SignUpComponent() {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate({ to: "/dashboard" });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // `defaultValues` matter now that the fields are driven by `FormField`'s
  // controller: without them the inputs would start uncontrolled and React
  // would warn on the first keystroke.
  const form = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });

  const signUpMutation = useMutation({
    mutationFn: signUpUser,
    onSuccess: (_, variables) => {
      navigate({
        to: "/confirm-email",
        search: { email: variables.email, code: "" },
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || i18n._("Sign up failed"));
    },
  });

  const onSubmit = (data: SignUpForm) => {
    signUpMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone?.trim() || undefined,
      password: data.password,
    });
  };

  const handleGoogleAuth = () => {
    const apiBaseUrl = ENV.VITE_API_BASE_URL;
    const redirectTo = `${window.location.origin}/dashboard`;
    const authUrl = new URL(`${apiBaseUrl}/auth/google`);
    authUrl.searchParams.set("redirect", redirectTo);
    window.location.href = authUrl.toString();
  };

  const isPending = signUpMutation.isPending;

  return (
    <div className="flex min-h-screen bg-(--brand-white)">
      {/* Left — kitchen photo with Figma-matched overlays */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=800&h=1200&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Semi-transparent white wash */}
        <div className="absolute inset-0 bg-(--color-scrim-photo-wash)" />
        {/* Gradient fading photo to white on the right edge */}
        <div className="absolute inset-0 bg-linear-to-r from-transparent to-white" />
      </div>

      {/* Right — form panel */}
      <div
        className="flex-1 flex flex-col items-center justify-between py-20 px-8 lg:px-30 bg-(--brand-white)"
      >
        {/* Logo */}
        <div className="self-start">
          <YesChefLogo />
        </div>

        {/* Form block */}
        <div className="w-full max-w-122 flex flex-col gap-6">
          {/* Heading */}
          <div className="flex flex-col gap-2 text-center">
            <h1
              className="font-bold leading-tight text-(--brand-black)"
              style={{ fontSize: 48 }}
            >
              {i18n._("Sign Up to Yes,Chef!")}
            </h1>
            <p className="text-base text-(--gray-500)">
              {i18n._(
                "Create your account to start managing recipes and kitchen operations.",
              )}
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {/* Google button */}
            <button
              type="button"
              onClick={handleGoogleAuth}
              disabled={isPending}
              className="w-full h-11 flex items-center justify-center gap-2.5 font-medium text-sm rounded-[6px] bg-(--color-surface-content) hover:bg-(--color-surface-muted) transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-(--gray-300) text-(--brand-black)"
              style={{
                boxShadow: "0px 1px 2px 0px rgba(16,24,40,0.05)" }}
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {i18n._("Continue with Google")}
            </button>

            {/* OR divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-(--gray-200)" />
              <span
                className="text-sm font-medium text-(--gray-400)"
              >
                {i18n._("or")}
              </span>
              <div className="flex-1 border-t border-(--gray-200)" />
            </div>

            {/* Fields */}
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-2.5"
                noValidate
              >
                {/* First name + Last name */}
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("First name")}</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder={i18n._("Enter your first name...")}
                            className="figma-input"
                            disabled={isPending}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("Last name")}</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder={i18n._("Enter your last name...")}
                            className="figma-input"
                            disabled={isPending}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Email + Phone */}
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("Email")}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={i18n._("Enter your email address...")}
                            className="figma-input"
                            disabled={isPending}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={() => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("Phone")}</FormLabel>
                        {/* PhoneInput owns its own controller (it splits the
                            country code out of the stored value), so it is not
                            wrapped in FormControl and takes `control` directly. */}
                        <PhoneInput
                          control={form.control}
                          name="phone"
                          disabled={isPending}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Password + Confirm password */}
                <div className="flex gap-3">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("Password")}</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder={i18n._("Enter your password...")}
                              className="figma-input pr-10"
                              disabled={isPending}
                              {...field}
                            />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem className="flex-1 min-w-0">
                        <FormLabel>{i18n._("Confirm password")}</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder={i18n._("Confirm your password...")}
                              className="figma-input pr-10"
                              disabled={isPending}
                              {...field}
                            />
                          </FormControl>
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-[6px] mt-1 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 bg-(--brand-black)"
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {i18n._("Creating account...")}
                    </>
                  ) : (
                    <>
                      {i18n._("Sign up")}
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </Form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-sm text-(--gray-500)">
          {i18n._("Already part of Yes, Chef?")}{" "}
          <Link
            to="/login"
            className="font-semibold hover:underline text-(--gray-600)"
          >
            {i18n._("Login")}
          </Link>
        </p>
      </div>

    </div>
  );
}

